const STAGGER_STEP_MS = 90;
const MAX_STAGGER_MS = 360;

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @type {IntersectionObserver | null} */
let observer = null;

/**
 * Reveal an element and stop observing it.
 * @param {Element} element
 */
function reveal(element) {
  element.classList.add('scroll-reveal--visible');
  observer?.unobserve(element);
}

/**
 * @returns {IntersectionObserver}
 */
function getObserver() {
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) reveal(entry.target);
        }
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );
  }
  return observer;
}

/**
 * Top-level `.shopify-block` elements within a section: excludes blocks nested
 * inside another block (e.g. a card inside a grid group), which reveal as a
 * single unit with their parent instead of double-animating.
 * @param {Element} section
 * @returns {Element[]}
 */
function getTopLevelBlocks(section) {
  return Array.from(section.querySelectorAll('.shopify-block')).filter(
    (el) => !el.parentElement?.closest('.shopify-block')
  );
}

/**
 * Marks a section and its top-level content blocks for scroll-reveal and starts observing them.
 * Safe to call repeatedly: already-marked elements are skipped.
 * @param {Element} section
 */
function prepareSection(section) {
  if (!(section instanceof HTMLElement)) return;

  if (!section.classList.contains('scroll-reveal')) {
    section.classList.add('scroll-reveal', 'scroll-reveal--section');
    getObserver().observe(section);
  }

  getTopLevelBlocks(section).forEach((block, index) => {
    if (block.classList.contains('scroll-reveal')) return;
    const delay = Math.min(index * STAGGER_STEP_MS, MAX_STAGGER_MS);
    block.style.setProperty('--reveal-delay', `${delay}ms`);
    block.classList.add('scroll-reveal', 'scroll-reveal--block');
    getObserver().observe(block);
  });
}

function scan() {
  const main = document.getElementById('MainContent');
  if (!main) return;
  main.querySelectorAll('.shopify-section').forEach(prepareSection);
}

function init() {
  if (prefersReducedMotion) return;

  scan();

  const main = document.getElementById('MainContent');
  if (!main) return;

  // Re-scan when the theme editor swaps section markup or new content is injected.
  let rescanTimer;
  const mutationObserver = new MutationObserver(() => {
    clearTimeout(rescanTimer);
    rescanTimer = setTimeout(scan, 50);
  });
  mutationObserver.observe(main, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
